import {
  HEADER_ICON,
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import {
  AlertTriangle,
  Ban,
  Building2,
  CalendarCheck,
  CheckCircle,
  CheckCircle2,
  Columns,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  FileText,
  LayoutList,
  ListFilter,
  ChevronDown,
  PhoneCall,
  PlusCircle,
  RefreshCw,
  Search,
  Settings2,
  Tag,
  Target,
  Trash2,
  TrendingUp,
  Upload,
  Users,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ActionDropdown,
  Badge,
  Button,
  CampaignLeadDetailsModal,
  EmptyState,
  Input,
  Modal,
  PageLayout,
  Pagination,
  Select,
} from "../components";
import { CampaignLeadsSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService from "../shared/apiService";
import { cn } from "../utils/cn";
import {
  PlatformIcon,
  getPlatformConfig,
  getPlatformName,
} from "../utils/platformIcons";
import LeadKanban, { TagManagerModal } from "./LeadKanban";
import type { LeadKanbanRef } from "./LeadKanban";

/** Como o lead chegou (canal), lista fechada igual à do Virtus Chat e do Leadflow. */
const OFFLINE_LEAD_SOURCES = [
  {
    value: "referral_customer",
    label: "Indicação de cliente",
    group: "Indicação",
  },
  {
    value: "referral_partner",
    label: "Indicação de parceiro/corretor",
    group: "Indicação",
  },
  {
    value: "referral_employee",
    label: "Indicação de funcionário",
    group: "Indicação",
  },
  {
    value: "influencer",
    label: "Influenciador/parceria de conteúdo",
    group: "Indicação",
  },
  { value: "event", label: "Evento/Feira", group: "Presencial" },
  {
    value: "walk_in",
    label: "Visita à loja/escritório/stand",
    group: "Presencial",
  },
  { value: "sales_desk", label: "Plantão de vendas", group: "Presencial" },
  {
    value: "inbound_call",
    label: "Ligação recebida",
    group: "Contato recebido",
  },
  {
    value: "inbound_email",
    label: "E-mail recebido",
    group: "Contato recebido",
  },
  {
    value: "inbound_whatsapp",
    label: "WhatsApp (link, status ou grupo)",
    group: "Contato recebido",
  },
  {
    value: "company_website",
    label: "Site da empresa",
    group: "Contato recebido",
  },
  {
    value: "outbound_call",
    label: "Prospecção ativa (ligação)",
    group: "Prospecção",
  },
  {
    value: "outbound_field",
    label: "Prospecção ativa (porta a porta/campo)",
    group: "Prospecção",
  },
  {
    value: "purchased_list",
    label: "Lista comprada/parceira",
    group: "Prospecção",
  },
  { value: "ads_google", label: "Anúncio no Google", group: "Anúncios" },
  { value: "ads_instagram", label: "Anúncio no Instagram", group: "Anúncios" },
  { value: "ads_facebook", label: "Anúncio no Facebook", group: "Anúncios" },
  { value: "ads_linkedin", label: "Anúncio no LinkedIn", group: "Anúncios" },
  { value: "ads_tiktok", label: "Anúncio no TikTok", group: "Anúncios" },
  { value: "ads_youtube", label: "Anúncio no YouTube", group: "Anúncios" },
  {
    value: "organic_google_search",
    label: "Busca no Google",
    group: "Digital orgânico",
  },
  {
    value: "organic_google_business",
    label: "Google Meu Negócio (perfil no Google/Maps)",
    group: "Digital orgânico",
  },
  { value: "organic_instagram", label: "Instagram", group: "Digital orgânico" },
  { value: "organic_facebook", label: "Facebook", group: "Digital orgânico" },
  { value: "organic_linkedin", label: "LinkedIn", group: "Digital orgânico" },
  { value: "organic_tiktok", label: "TikTok", group: "Digital orgânico" },
  { value: "organic_youtube", label: "YouTube", group: "Digital orgânico" },
  { value: "ai_chatgpt", label: "ChatGPT", group: "Assistente de IA" },
  { value: "ai_gemini", label: "Gemini", group: "Assistente de IA" },
  { value: "ai_claude", label: "Claude", group: "Assistente de IA" },
  { value: "ai_copilot", label: "Copilot", group: "Assistente de IA" },
  { value: "ai_perplexity", label: "Perplexity", group: "Assistente de IA" },
  { value: "portal_zap", label: "ZAP Imóveis", group: "Portais" },
  { value: "portal_vivareal", label: "Viva Real", group: "Portais" },
  { value: "portal_olx", label: "OLX", group: "Portais" },
  { value: "portal_imovelweb", label: "Imovelweb", group: "Portais" },
  { value: "portal_chavesnamao", label: "Chaves na Mão", group: "Portais" },
  { value: "tv", label: "TV", group: "Mídia offline" },
  { value: "radio", label: "Rádio", group: "Mídia offline" },
  { value: "print_media", label: "Jornal/Revista", group: "Mídia offline" },
  { value: "press", label: "Imprensa/matéria", group: "Mídia offline" },
  { value: "ooh", label: "Outdoor/Mídia exterior", group: "Mídia offline" },
  {
    value: "flyer",
    label: "Panfleto/Material impresso",
    group: "Mídia offline",
  },
  {
    value: "returning_customer",
    label: "Cliente antigo/recompra",
    group: "Base própria",
  },
  { value: "own_database", label: "Base importada", group: "Base própria" },
];

interface CampaignLead {
  id: number;
  campaign_id?: number;
  external_campaign_id?: string;
  platform: string;
  tags?: number[];
  campaign?: {
    id: number;
    name: string;
    platform: string | null;
    status: string | null;
    source_post_id?: number | null;
  } | null;
  name?: string;
  email: string;
  phone?: string;
  company?: string;
  job_title?: string;
  notes?: string;
  interest?: string;
  origin_platform?: string;
  origin_description?: string;
  custom_fields?: Record<string, unknown>;
  status: string;
  qualification_status?: string;
  qualification_reason?: string;
  lead_score: number;
  lead_source_system?: string;
  external_lead_id?: string;
  metadata?: Record<string, unknown>;
  leadflow_lead_id?: string | null;
  leadflow_sync_status?: string | null;
  crm_contact_id?: string;
  crm_provider?: string;
  crm_sync_status: string;
  synced_to_crm_at?: string;
  created_at: string;
  qualified_at?: string;
  is_duplicate?: boolean;
  is_cancelled?: boolean;
  is_fraud?: boolean;
  /** Oculto no Gerenciar Leads (fora das listas, públicos e conversões; pode voltar). */
  is_hidden?: boolean;
  duplicate_of_lead_id?: number;
  fraud_reason?: string;
  cancelled_reason?: string;
}

const CampaignLeads: React.FC = () => {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const kanbanRef = useRef<LeadKanbanRef>(null);
  const [canEditStages, setCanEditStages] = useState(true);
  const [leads, setLeads] = useState<CampaignLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<number | null>(null);
  // Feedback de loading compartilhado pelas ações da coluna "Gestão"
  // (CAPI, duplicado, fraude) — antes clicar não dava nenhum indício visual
  // de que a ação estava em andamento até o toast final aparecer.
  const [rowActionLoading, setRowActionLoading] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const campaignIdFromUrl = searchParams.get("campaign");
  const [viewingLead, setViewingLead] = useState<CampaignLead | null>(null);

  const [filters, setFilters] = useState({
    status: "",
    qualification_status: "",
    platform: "",
    campaign_id: campaignIdFromUrl || "",
    is_duplicate: "",
    is_cancelled: "",
    is_fraud: "",
    show_hidden: "",
    search: "",
  });

  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        return { ...prev, search: searchInput };
      });
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const [stats, setStats] = useState({
    total_leads: 0,
    total_unique: 0,
    qualified_leads: 0,
    disqualified_leads: 0,
    synced_to_crm: 0,
    duplicate_leads: 0,
    cancelled_leads: 0,
    fraud_leads: 0,
    by_platform: {} as Record<string, number>,
    by_status: {} as Record<string, number>,
  });

  const KPI_COLORS = {
    purple: {
      border: "border-purple-200",
      accent: "bg-purple-600",
      text: "text-purple-700",
      iconBg: "bg-purple-100",
      iconText: "text-purple-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(168,85,247,0.18)]",
      subBg: "bg-purple-50",
      subText: "text-purple-500",
    },
    emerald: {
      border: "border-emerald-200",
      accent: "bg-emerald-600",
      text: "text-emerald-700",
      iconBg: "bg-emerald-100",
      iconText: "text-emerald-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(16,185,129,0.18)]",
      subBg: "bg-emerald-50",
      subText: "text-emerald-500",
    },
    rose: {
      border: "border-rose-200",
      accent: "bg-rose-600",
      text: "text-rose-700",
      iconBg: "bg-rose-100",
      iconText: "text-rose-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(244,63,94,0.18)]",
      subBg: "bg-rose-50",
      subText: "text-rose-500",
    },
    indigo: {
      border: "border-indigo-200",
      accent: "bg-indigo-600",
      text: "text-indigo-700",
      iconBg: "bg-indigo-100",
      iconText: "text-indigo-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(99,102,241,0.18)]",
      subBg: "bg-indigo-50",
      subText: "text-indigo-500",
    },
    amber: {
      border: "border-amber-200",
      accent: "bg-amber-600",
      text: "text-amber-700",
      iconBg: "bg-amber-100",
      iconText: "text-amber-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(245,158,11,0.18)]",
      subBg: "bg-amber-50",
      subText: "text-amber-500",
    },
    gray: {
      border: "border-purple-200",
      accent: "bg-gray-500",
      text: "text-gray-700",
      iconBg: "bg-gray-100",
      iconText: "text-gray-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(107,114,128,0.18)]",
      subBg: "bg-gray-50",
      subText: "text-gray-500",
    },
    red: {
      border: "border-red-200",
      accent: "bg-red-600",
      text: "text-red-700",
      iconBg: "bg-red-100",
      iconText: "text-red-600",
      hoverGlow: "hover:shadow-[0_4px_24px_rgba(239,68,68,0.18)]",
      subBg: "bg-red-50",
      subText: "text-red-500",
    },
  };

  const overviewStats = [
    {
      label: t("Leads Únicos"),
      val:
        stats.total_unique !== undefined && stats.total_unique !== null
          ? String(stats.total_unique)
          : stats.total_leads !== undefined && stats.total_leads !== null
            ? String(stats.total_leads)
            : "N/D",
      icon: Users,
      color: "purple" as const,
      badge: t("Real"),
      footer: (
        <>
          {t("De um total de")}{" "}
          <span className="font-bold text-purple-600">
            {stats.total_leads ?? 0}
          </span>{" "}
          {t("capturas")}
        </>
      ),
    },
    {
      label: t("Qualificados"),
      val:
        stats.qualified_leads !== undefined && stats.qualified_leads !== null
          ? String(stats.qualified_leads)
          : "N/D",
      icon: CheckCircle2,
      color: "emerald" as const,
      footer: t("Prontos para conversão"),
    },
    {
      label: t("Desqualificados"),
      val:
        stats.disqualified_leads !== undefined &&
        stats.disqualified_leads !== null
          ? String(stats.disqualified_leads)
          : "N/D",
      icon: XCircle,
      color: "rose" as const,
      footer: t("Fora do perfil desejado"),
    },
    {
      label: t("Sincronizados CRM"),
      val:
        stats.synced_to_crm !== undefined && stats.synced_to_crm !== null
          ? String(stats.synced_to_crm)
          : "N/D",
      icon: RefreshCw,
      color: "indigo" as const,
      footer: t("Integrados com sucesso"),
    },
  ];

  const qualityStats = [
    {
      label: t("Leads Duplicados"),
      val:
        stats.duplicate_leads !== undefined && stats.duplicate_leads !== null
          ? String(stats.duplicate_leads)
          : "0",
      icon: Copy,
      color: "amber" as const,
      badge: t("Repetidos"),
    },
    {
      label: t("Leads Cancelados"),
      val:
        stats.cancelled_leads !== undefined && stats.cancelled_leads !== null
          ? String(stats.cancelled_leads)
          : "0",
      icon: Ban,
      color: "gray" as const,
      badge: t("Pausados"),
    },
    {
      label: t("Possíveis Fraudes"),
      val:
        stats.fraud_leads !== undefined && stats.fraud_leads !== null
          ? String(stats.fraud_leads)
          : "0",
      icon: AlertTriangle,
      color: "red" as const,
      badge: t("Risco Crítico"),
    },
  ];

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPlatform, setUploadPlatform] = useState<string>("facebook");
  const [uploadQualification, setUploadQualification] =
    useState<string>("qualified");
  const [createCustomAudience, setCreateCustomAudience] = useState(false);
  const [customAudienceName, setCustomAudienceName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    imported: number;
    duplicates_skipped: number;
    errors: Array<{ row: number; message: string }>;
    message?: string;
    audience_id?: string;
    audience_error?: string;
  } | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Visão: lista ou kanban
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [kanbanRefreshKey, setKanbanRefreshKey] = useState(0);
  // Tags personalizadas
  const [availableTags, setAvailableTags] = useState<
    { id: number; name: string; color: string }[]
  >([]);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [kpisCollapsed, setKpisCollapsed] = useState(false);

  // Modal: Novo lead manual
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    job_title: "",
    platform: "facebook",
    origin_description: "",
    offline_source: "",
    funnel_status: "new",
    interest: "",
    notes: "",
    additional_emails: [] as string[],
    additional_phones: [] as string[],
  });

  // Modal: Atribuir Tags a um lead
  const [tagPickerLead, setTagPickerLead] = useState<CampaignLead | null>(null);
  const [pendingTags, setPendingTags] = useState<number[]>([]);
  const [savingTags, setSavingTags] = useState(false);

  // Modal: Criar Público Meta
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [creatingAudience, setCreatingAudience] = useState(false);
  const [audienceForm, setAudienceForm] = useState<{
    audience_name: string;
    use_selected: boolean;
    qualification_status: "" | "qualified" | "disqualified";
    statuses: string[];
    tag_ids: number[];
    // Funil do Virtus Chat: colunas dele (id da etapa, "won" ou "lost") no lugar das etapas daqui.
    pipeline_id: string;
    deal_columns: string[];
  }>({
    audience_name: "",
    use_selected: false,
    qualification_status: "",
    statuses: [],
    tag_ids: [],
    pipeline_id: "",
    deal_columns: [],
  });

  // Etapas do kanban (cores e labels dos badges na lista)
  const [stageColorMap, setStageColorMap] = useState<Record<string, string>>({
    new: "#6366f1",
    contacted: "#3b82f6",
    negotiating: "#f59e0b",
    visit_scheduled: "#a855f7",
    proposal_sent: "#06b6d4",
    qualified: "#10b981",
    closed: "#22c55e",
    lost: "#ef4444",
  });
  const [stageLabelMap, setStageLabelMap] = useState<Record<string, string>>({
    new: "Novo",
    contacted: "Contatado",
    negotiating: "Negociando",
    visit_scheduled: "Visita Agendada",
    proposal_sent: "Proposta Enviada",
    qualified: "Qualificado",
    closed: "Fechado",
    lost: "Perdido",
  });
  const [kanbanStages, setKanbanStages] = useState<
    { key: string; label: string; color: string }[]
  >([]);
  // Funis do Virtus Chat da conta (vazio = conta sem Chat).
  const [chatPipelines, setChatPipelines] = useState<
    {
      id: string;
      name: string;
      stages: { id: string; name: string; color?: string }[];
    }[]
  >([]);

  const audiencePipeline = chatPipelines.find(
    (p) => p.id === audienceForm.pipeline_id,
  );
  const audienceStageOptions = audiencePipeline
    ? [
        ...audiencePipeline.stages.map((s) => ({
          key: s.id,
          label: s.name,
          color: s.color || "#6366f1",
        })),
        { key: "won", label: t("Fechado"), color: "#22c55e" },
        { key: "lost", label: t("Perdido"), color: "#ef4444" },
      ]
    : kanbanStages.map((s) => ({
        ...s,
        color: stageColorMap[s.key] || "#6366f1",
      }));

  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const contextIdRef = useRef(contextId);

  // Sync ref with current contextId
  contextIdRef.current = contextId;

  const [lastContextId, setLastContextId] = useState(contextId);

  if (contextId !== lastContextId) {
    setLeads([]);
    setLoading(true);
    setPage(1);
    setLastContextId(contextId);
  }

  const loadLeads = useCallback(async () => {
    const requestContextId = contextIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeContext?.organization?.id)
        params.append("organization_id", String(activeContext.organization.id));
      if (activeContext?.clientCompany?.id)
        params.append("company_id", String(activeContext.clientCompany.id));
      if (filters.status) params.append("status", filters.status);
      if (filters.qualification_status)
        params.append("qualification_status", filters.qualification_status);
      if (filters.platform) params.append("platform", filters.platform);
      if (filters.campaign_id)
        params.append("campaign_id", filters.campaign_id);
      if (filters.is_duplicate)
        params.append("is_duplicate", filters.is_duplicate);
      if (filters.is_cancelled)
        params.append("is_cancelled", filters.is_cancelled);
      if (filters.is_fraud) params.append("is_fraud", filters.is_fraud);
      if (filters.show_hidden)
        params.append("show_hidden", filters.show_hidden);
      if (filters.search) params.append("search", filters.search);

      params.append("limit", pageSize.toString());
      params.append("offset", ((page - 1) * pageSize).toString());

      const data = await apiService.get(`/api/ads/leads?${params.toString()}`);

      // CHECK: If context changed while fetching, ignore results
      if (contextIdRef.current !== requestContextId) {
        return;
      }

      if (data.success) {
        setLeads(data.leads || []);
        setTotalCount(data.total || 0);
      }
    } catch (error: any) {
      // Only show error if context is still the same
      if (contextIdRef.current === requestContextId) {
        showToastError(error.message || t("Erro ao carregar leads"));
      }
    } finally {
      // Only toggle loading if context is still the same
      if (contextIdRef.current === requestContextId) {
        setLoading(false);
      }
    }
  }, [activeContext, filters, page, pageSize]);

  const switchView = useCallback(
    (mode: "list" | "kanban") => {
      setViewMode(mode);
      if (mode === "list") loadLeads();
      else setKanbanRefreshKey((k) => k + 1);
    },
    [loadLeads],
  );

  const loadStats = useCallback(async () => {
    const requestContextId = contextIdRef.current;
    try {
      const params = new URLSearchParams();
      if (activeContext?.organization?.id)
        params.append("organization_id", String(activeContext.organization.id));
      if (activeContext?.clientCompany?.id)
        params.append("company_id", String(activeContext.clientCompany.id));

      const data = await apiService.get(
        `/api/ads/leads/stats?${params.toString()}`,
      );

      // CHECK: If context changed while fetching, ignore results
      if (contextIdRef.current !== requestContextId) {
        return;
      }

      if (data.success && data.stats) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
    }
  }, [activeContext]);

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [loadLeads, loadStats]);

  // Carregar cores das etapas do kanban para usar nos badges da lista
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeContext?.organization?.id)
      params.append("organization_id", String(activeContext.organization.id));
    if (activeContext?.clientCompany?.id)
      params.append("company_id", String(activeContext.clientCompany.id));
    apiService
      .get(`/api/ads/leads/stages?${params}`)
      .then((data) => {
        if (data.success && data.stages?.length) {
          const colorMap: Record<string, string> = {};
          const labelMap: Record<string, string> = {};
          data.stages.forEach(
            (s: { key: string; color: string; label: string }) => {
              colorMap[s.key] = s.color;
              labelMap[s.key] = s.label;
            },
          );
          setStageColorMap(colorMap);
          setStageLabelMap(labelMap);
          setKanbanStages(
            data.stages.map(
              (s: { key: string; label: string; color: string }) => ({
                key: s.key,
                label: s.label,
                color: s.color,
              }),
            ),
          );
        }
      })
      .catch(() => {
        /* usa cores padrão */
      });
    apiService
      .get("/api/ads/leads/pipelines")
      .then((data) =>
        setChatPipelines(data?.success ? data.pipelines || [] : []),
      )
      .catch(() => setChatPipelines([]));
  }, [activeContext]);

  // Carregar tags disponíveis (independente da visão ativa)
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeContext?.organization?.id)
      params.append("organization_id", String(activeContext.organization.id));
    if (activeContext?.clientCompany?.id)
      params.append("company_id", String(activeContext.clientCompany.id));
    apiService
      .get(`/api/ads/leads/tags?${params}`)
      .then((data) => {
        if (data.success && Array.isArray(data.tags)) {
          setAvailableTags(data.tags);
        }
      })
      .catch(() => {
        /* mantém vazio se falhar */
      });
  }, [activeContext]);

  // Reset pagination when filters or context change
  useEffect(() => {
    setPage(1);
  }, [filters, contextId]);

  const handleUploadExcel = async () => {
    if (!uploadFile) {
      showToastError(t("Selecione um arquivo .xlsx"));
      return;
    }
    const token =
      localStorage.getItem("access_token") || localStorage.getItem("token");
    if (!token) {
      showToastError(t("Token não encontrado. Faça login novamente."));
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      form.append("platform", uploadPlatform);
      form.append("qualification_status", uploadQualification);
      form.append("skip_duplicates", "true");
      form.append(
        "create_custom_audience",
        createCustomAudience ? "true" : "false",
      );
      if (createCustomAudience) {
        form.append("custom_audience_name", customAudienceName);
      }
      const data = await apiService.post("/api/ads/leads/upload-excel", form, {
        timeout: 600000,
      }); // 10 minutes for large uploads

      if (!data.success) {
        throw new Error(data.error || t("Erro ao importar planilha"));
      }
      setUploadResult({
        imported: data.imported ?? 0,
        duplicates_skipped: data.duplicates_skipped ?? 0,
        errors: data.errors ?? [],
        message: data.message,
        audience_id: data.audience_id,
        audience_error: data.audience_error,
      });
      if (data.imported > 0) {
        showToastSuccess(
          t(
            "{count} lead(s) importado(s). Use para CAPI, Lookalike ou Customer Match.",
            { count: data.imported },
          ),
        );
        loadLeads();
        loadStats();

        // Close modal only if there are no errors to review
        if (!data.errors || data.errors.length === 0) {
          setTimeout(() => {
            setUploadModalOpen(false);
            setUploadFile(null);
            setUploadResult(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }, 1000);
        }
      }
    } catch (error: any) {
      showToastError(error.message || t("Erro ao importar planilha"));
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadFile(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCreateLead = async () => {
    if (!createForm.email.trim()) {
      showToastError(t("Email é obrigatório"));
      return;
    }
    if (!createForm.offline_source) {
      showToastError(t("Escolha como o lead chegou"));
      return;
    }
    setCreating(true);
    try {
      const extraEmails = createForm.additional_emails
        .map((e) => e.trim())
        .filter((e) => e.includes("@"));
      const extraPhones = createForm.additional_phones
        .map((p) => p.trim())
        .filter(Boolean);
      const customFields: Record<string, unknown> = {};
      if (createForm.notes.trim())
        customFields["notes"] = createForm.notes.trim();
      if (createForm.interest.trim())
        customFields["interest"] = createForm.interest.trim();
      if (
        createForm.platform === "other" &&
        createForm.origin_description.trim()
      )
        customFields["origin_description"] =
          createForm.origin_description.trim();
      const payload: Record<string, unknown> = {
        name: createForm.name.trim() || undefined,
        email: createForm.email.trim(),
        phone: createForm.phone.trim() || undefined,
        company: createForm.company.trim() || undefined,
        job_title: createForm.job_title.trim() || undefined,
        platform:
          createForm.platform === "other" ? "other" : createForm.platform,
        funnel_status: createForm.funnel_status,
        offline_source: createForm.offline_source,
        lead_source_system: "manual",
        additional_emails: extraEmails.length ? extraEmails : undefined,
        additional_phones: extraPhones.length ? extraPhones : undefined,
        custom_fields: Object.keys(customFields).length
          ? customFields
          : undefined,
      };
      const data = await apiService.post("/api/ads/leads", payload);
      if (!data.success) throw new Error(data.error || t("Erro ao criar lead"));
      showToastSuccess(t("Lead criado com sucesso!"));
      setCreateModalOpen(false);
      setCreateForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        job_title: "",
        platform: "facebook",
        origin_description: "",
        offline_source: "",
        funnel_status: "new",
        interest: "",
        notes: "",
        additional_emails: [],
        additional_phones: [],
      });
      loadLeads();
      loadStats();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao criar lead"));
    } finally {
      setCreating(false);
    }
  };

  const handleOpenTagPicker = (lead: CampaignLead) => {
    setTagPickerLead(lead);
    setPendingTags(lead.tags || []);
  };

  const handleSaveLeadTags = async () => {
    if (!tagPickerLead) return;
    setSavingTags(true);
    try {
      const data = await apiService.patch(
        `/api/ads/leads/${tagPickerLead.id}/tags`,
        { tags: pendingTags },
      );
      if (!data.success)
        throw new Error(data.error || t("Erro ao salvar tags"));
      setLeads((prev) =>
        prev.map((l) =>
          l.id === tagPickerLead.id ? { ...l, tags: pendingTags } : l,
        ),
      );
      showToastSuccess(t("Tags atualizadas!"));
      setTagPickerLead(null);
    } catch (error: any) {
      showToastError(error.message || t("Erro ao salvar tags"));
    } finally {
      setSavingTags(false);
    }
  };

  const handleCreateAudience = async () => {
    if (!audienceForm.audience_name.trim()) {
      showToastError(t("Nome do público é obrigatório"));
      return;
    }
    setCreatingAudience(true);
    try {
      const filters: Record<string, unknown> = {};

      if (audienceForm.use_selected && selectedLeads.length > 0) {
        filters.lead_ids = selectedLeads;
      } else {
        if (audienceForm.qualification_status)
          filters.qualification_status = audienceForm.qualification_status;
        if (audienceForm.pipeline_id && audienceForm.deal_columns.length) {
          filters.pipeline_id = audienceForm.pipeline_id;
          filters.deal_columns = audienceForm.deal_columns;
        } else if (audienceForm.statuses.length)
          filters.statuses = audienceForm.statuses;
        if (audienceForm.tag_ids.length) {
          filters.tag_ids = audienceForm.tag_ids;
        }
      }

      const data = await apiService.post("/api/ads/meta/audiences/from-leads", {
        audience_name: audienceForm.audience_name.trim(),
        filters,
      });

      if (!data.success) {
        if (data.error_code === 471) {
          showToastError(
            t(
              "Audiência bloqueada pela Meta (código 471): esta conta não pode criar públicos com este tipo de conteúdo. Verifique as políticas de dados sensíveis na sua conta de anúncios.",
            ),
          );
        } else {
          throw new Error(data.error || t("Erro ao criar público"));
        }
        return;
      }

      showToastSuccess(
        t('Público "{name}" criado com {count} leads!', {
          name: audienceForm.audience_name.trim(),
          count: data.leads_count,
        }),
      );
      setAudienceModalOpen(false);
      setAudienceForm({
        audience_name: "",
        use_selected: false,
        qualification_status: "",
        statuses: [],
        tag_ids: [],
        pipeline_id: "",
        deal_columns: [],
      });
      setSelectedLeads([]);
    } catch (error: any) {
      showToastError(error.message || t("Erro ao criar público na Meta"));
    } finally {
      setCreatingAudience(false);
    }
  };

  const handleDownloadTemplate = async () => {
    // Token check is handled by apiService interceptor
    // const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    // if (!token) {
    //     showToastError('Token não encontrado. Faça login novamente.');
    //     return;
    // }
    setDownloadingTemplate(true);
    try {
      await apiService.downloadFile(
        "/api/ads/leads/upload-excel-template",
        "modelo_leads_capi.xlsx",
      );
      showToastSuccess(t("Modelo baixado com sucesso!"));
    } catch (error: any) {
      showToastError(error.message || t("Erro ao baixar modelo"));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleLeadUpdate = async (
    leadId: number,
    data: Record<string, unknown>,
  ) => {
    const res = await apiService.patch(`/api/ads/leads/${leadId}/info`, data);
    if (!res.success) throw new Error(res.error || t("Erro ao salvar dados"));
    const updated = res.lead as Partial<CampaignLead>;
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)),
    );
    setViewingLead((prev) =>
      prev && prev.id === leadId ? { ...prev, ...updated } : prev,
    );
    showToastSuccess(t("Dados atualizados!"));
  };

  const handleModalTagsChange = async (leadId: number, tagIds: number[]) => {
    const res = await apiService.patch(`/api/ads/leads/${leadId}/tags`, {
      tags: tagIds,
    });
    if (!res.success) throw new Error(res.error || t("Erro ao salvar tags"));
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, tags: tagIds } : l)),
    );
    setViewingLead((prev) =>
      prev && prev.id === leadId ? { ...prev, tags: tagIds } : prev,
    );
  };

  const handleStageChange = async (leadId: number, stage: string) => {
    try {
      const data = await apiService.patch(`/api/ads/leads/${leadId}/status`, {
        status: stage,
      });
      if (!data.success)
        throw new Error(data.error || t("Erro ao atualizar etapa"));
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: stage } : l)),
      );
      setViewingLead((prev) =>
        prev && prev.id === leadId ? { ...prev, status: stage } : prev,
      );
      showToastSuccess(t("Etapa atualizada!"));
    } catch (error: any) {
      showToastError(error.message || t("Erro ao atualizar etapa"));
    }
  };

  const handleQualificationChange = async (
    leadId: number,
    newStatus: string,
  ) => {
    try {
      const data = await apiService.patch(`/api/ads/leads/${leadId}/qualify`, {
        qualification_status: newStatus,
      });
      if (!data.success)
        throw new Error(data.error || t("Erro ao atualizar qualificação"));
      const qs = newStatus || undefined;
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, qualification_status: qs } : l,
        ),
      );
      setViewingLead((prev) =>
        prev && prev.id === leadId
          ? { ...prev, qualification_status: qs }
          : prev,
      );
      showToastSuccess(t("Qualificação atualizada!"));
    } catch (error: any) {
      showToastError(error.message || t("Erro ao atualizar qualificação"));
    }
  };

  const handleSyncToCRM = async (leadId: number) => {
    setSyncing(leadId);
    try {
      // Token check is handled by apiService interceptor
      // const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const data = await apiService.post(`/api/ads/leads/${leadId}/sync-crm`);

      if (!data.success) {
        throw new Error(data.error || t("Erro ao sincronizar com CRM"));
      }

      showToastSuccess(t("Lead sincronizado com CRM com sucesso!"));
      loadLeads();
      loadStats();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao sincronizar com CRM"));
    } finally {
      setSyncing(null);
    }
  };
  const handleSendToLeadflow = async (
    leadId: number,
    offlineSource?: string,
  ) => {
    try {
      const data = await apiService.post(
        `/api/ads/leads/${leadId}/send-to-leadflow`,
        {
          offline_source: offlineSource,
        },
      );
      if (!data.success)
        throw new Error(data.error || t("Erro ao enviar ao LeadFlow"));
      if (data.status === "pending") {
        showToastSuccess(
          t("LeadFlow indisponível agora: o lead vai nos próximos minutos."),
        );
      } else {
        showToastSuccess(t("Lead enviado ao LeadFlow!"));
      }
      if (data.lead) setViewingLead(data.lead);
      loadLeads();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao enviar ao LeadFlow"));
    }
  };
  const handleMarkDuplicate = async (leadId: number, forceState?: boolean) => {
    if (rowActionLoading) return;
    setRowActionLoading(leadId);
    try {
      const lead = leads.find((l) => l.id === leadId);
      const newState =
        forceState !== undefined ? forceState : !lead?.is_duplicate;

      const data = await apiService.post(
        `/api/ads/leads/${leadId}/mark-duplicate`,
        { is_duplicate: newState },
      );
      if (data.success) {
        showToastSuccess(
          newState
            ? t("Lead marcado como duplicado!")
            : t("Marcação de duplicado removida!"),
        );
        loadLeads();
        loadStats();
      }
    } catch (error: any) {
      showToastError(error.message || t("Erro ao processar duplicado"));
    } finally {
      setRowActionLoading(null);
    }
  };

  const handleMarkFraud = async (leadId: number, forceState?: boolean) => {
    try {
      const lead = leads.find((l) => l.id === leadId);
      const newState = forceState !== undefined ? forceState : !lead?.is_fraud;

      let reason = undefined;
      if (newState && !forceState) {
        const { value: text, isConfirmed } = await Swal.fire({
          title: t("Reportar Fraude"),
          text: t(
            "Deseja marcar este lead como fraudulento? Isso ajudará na inteligência futura.",
          ),
          input: "text",
          inputLabel: t("Descrição da fraude (opcional):"),
          inputPlaceholder: t("Ex: Bot, número falso..."),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: t("Confirmar Fraude"),
          cancelButtonText: t("Voltar"),
          confirmButtonColor: "#EF4444",
        });

        if (!isConfirmed) return;
        reason = text || undefined;
      }

      setRowActionLoading(leadId);
      const data = await apiService.put(`/api/ads/leads/${leadId}`, {
        is_fraud: newState,
        fraud_reason: reason,
      });
      if (data.success) {
        showToastSuccess(
          newState
            ? t("Lead reportado como fraude!")
            : t("Flag de fraude removida!"),
        );
        loadLeads();
        loadStats();
      }
    } catch (error: any) {
      showToastError(error.message || t("Erro ao processar fraude"));
    } finally {
      setRowActionLoading(null);
    }
  };

  const hideLeads = async (ids: number[], hidden: boolean) => {
    try {
      const data = await apiService.post("/api/ads/leads/hide", {
        ids,
        hidden,
      });
      if (!data.success)
        throw new Error(data.error || t("Erro ao ocultar lead"));
      showToastSuccess(
        hidden
          ? t("{count} leads ocultados", { count: data.updated })
          : t("{count} leads visíveis de novo", { count: data.updated }),
      );
      loadLeads();
      loadStats();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao ocultar lead"));
    }
  };

  /** Lead que não pode ser apagado (veio de anúncio/integração ou já foi ao CRM/Chat): oferece ocultar. */
  const offerHide = async (ids: number[], message: string) => {
    const { isConfirmed } = await Swal.fire({
      title: t("Ocultar em vez de excluir?"),
      text: message,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: t("Ocultar"),
      cancelButtonText: t("Cancelar"),
    });
    if (isConfirmed) await hideLeads(ids, true);
  };

  const handleDeleteLead = async (leadId: number) => {
    const { isConfirmed } = await Swal.fire({
      title: t("Excluir Lead?"),
      text: t(
        "Só leads offline cadastrados ou importados aqui podem ser excluídos. Os outros podem ser ocultados.",
      ),
      icon: "error",
      showCancelButton: true,
      confirmButtonText: t("Sim, Excluir!"),
      cancelButtonText: t("Cancelar"),
      confirmButtonColor: "#EF4444",
    });

    if (!isConfirmed) return;

    try {
      const data = await apiService.delete(`/api/ads/leads/${leadId}`);
      if (!data.success)
        throw new Error(data.error || t("Erro ao excluir lead"));
      showToastSuccess(t("Lead excluído permanentemente!"));
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      loadStats();
    } catch (error: any) {
      if (error?.errorData?.code === "only_hide") {
        await offerHide([leadId], error.errorData.error || error.message);
        return;
      }
      showToastError(error.message || t("Erro ao excluir lead"));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedLeads.length) return;

    const { isConfirmed } = await Swal.fire({
      title: t("Excluir {count} Leads?", { count: selectedLeads.length }),
      text: t(
        "Só leads offline cadastrados ou importados aqui são excluídos. Os outros podem ser ocultados.",
      ),
      icon: "error",
      showCancelButton: true,
      confirmButtonText: t("Excluir em Massa"),
      confirmButtonColor: "#EF4444",
      cancelButtonText: t("Voltar"),
    });

    if (!isConfirmed) return;

    setDeletingBulk(true);
    try {
      const data = await apiService.post("/api/ads/leads/bulk-delete", {
        ids: selectedLeads,
      });
      if (!data.success)
        throw new Error(data.error || t("Erro na exclusão em massa"));
      if (data.deleted)
        showToastSuccess(
          t("{count} leads excluídos com sucesso!", { count: data.deleted }),
        );
      setSelectedLeads([]);
      loadLeads();
      loadStats();
      const kept: { id: number; code: string }[] = data.not_deleted || [];
      const hideable = kept
        .filter((k) => k.code === "only_hide")
        .map((k) => k.id);
      if (hideable.length)
        await offerHide(
          hideable,
          t(
            "{count} leads não podem ser excluídos (vieram de anúncio/integração ou já foram ao CRM/Chat).",
            {
              count: hideable.length,
            },
          ),
        );
      if (kept.length > hideable.length)
        showToastError(
          t(
            "{count} leads não foram excluídos agora; tente de novo em instantes.",
            {
              count: kept.length - hideable.length,
            },
          ),
        );
    } catch (error: any) {
      showToastError(error.message || t("Erro na exclusão em massa"));
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleExportLeads = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (activeContext?.organization?.id)
        params.append("organization_id", String(activeContext.organization.id));
      if (activeContext?.clientCompany?.id)
        params.append("company_id", String(activeContext.clientCompany.id));
      if (filters.status) params.append("status", filters.status);
      if (filters.qualification_status)
        params.append("qualification_status", filters.qualification_status);
      if (filters.platform) params.append("platform", filters.platform);
      if (filters.campaign_id)
        params.append("campaign_id", filters.campaign_id);
      if (filters.is_duplicate)
        params.append("is_duplicate", filters.is_duplicate);
      if (filters.is_cancelled)
        params.append("is_cancelled", filters.is_cancelled);
      if (filters.is_fraud) params.append("is_fraud", filters.is_fraud);
      if (filters.show_hidden)
        params.append("show_hidden", filters.show_hidden);

      const filename = `export_leads_${new Date().getTime()}.xlsx`;
      await apiService.downloadFile(
        `/api/ads/leads/export?${params.toString()}`,
        filename,
      );
      showToastSuccess(t("Relatório de Leads exportado!"));
    } catch (error: any) {
      showToastError(error.message || t("Erro ao exportar leads"));
    } finally {
      setExporting(false);
    }
  };

  const handleSyncCapi = async (leadId: number) => {
    if (rowActionLoading) return;
    setRowActionLoading(leadId);
    try {
      const data = await apiService.post(`/api/ads/leads/${leadId}/sync-capi`);
      if (data.success) {
        Swal.fire({
          icon: "success",
          title: t("Evento Sincronizado"),
          text: t(
            'Evento "{message}" enviado para as plataformas CAPI selecionadas.',
            { message: data.message },
          ),
          timer: 2000,
          showConfirmButton: false,
          position: "top-end",
          toast: true,
        });
      } else {
        Swal.fire(
          t("Erro"),
          data.error || t("Erro ao sincronizar CAPI"),
          "error",
        );
      }
    } catch (error) {
      console.error("CAPI sync error:", error);
      Swal.fire(
        t("Erro"),
        t("Erro de conexão com o servidor de CAPI"),
        "error",
      );
    } finally {
      setRowActionLoading(null);
    }
  };

  const getStatusBadge = (lead: CampaignLead) => {
    // Prioridade: fraude > cancelado > duplicado > qualificação
    if (lead.is_fraud) {
      return (
        <Badge
          variant="destructive"
          className="flex items-center gap-1.5 font-black px-3"
          size="sm"
        >
          <AlertTriangle className="h-3 w-3" />
          {t("Fraude")}
        </Badge>
      );
    }
    if (lead.is_cancelled) {
      return (
        <Badge
          variant="secondary"
          className="flex items-center gap-1.5 font-black px-3 bg-gray-100 border-purple-200 text-gray-600"
          size="sm"
        >
          <Ban className="h-3 w-3" />
          {t("Cancelado")}
        </Badge>
      );
    }
    if (lead.is_duplicate) {
      return (
        <Badge
          variant="outline"
          className="flex items-center gap-1.5 font-black px-3 border-orange-200 text-orange-600 bg-orange-50"
          size="sm"
        >
          <Copy className="h-3 w-3" />
          {t("Duplicado")}
        </Badge>
      );
    }
    if (lead.qualification_status === "qualified") {
      return (
        <Badge
          variant="success"
          className="flex items-center gap-1.5 font-black px-3"
          size="sm"
        >
          <CheckCircle2 className="h-3 w-3" />
          {t("Qualificado")}
        </Badge>
      );
    }
    if (lead.qualification_status === "disqualified") {
      return (
        <Badge
          variant="destructive"
          className="flex items-center gap-1.5 font-black px-3 opacity-80"
          size="sm"
        >
          <XCircle className="h-3 w-3" />
          {t("Desqualificado")}
        </Badge>
      );
    }
    // Badges das etapas do kanban — usa labels e cores carregados da API
    const color = stageColorMap[lead.status] || "#6366f1";
    const label = stageLabelMap[lead.status] || lead.status;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide"
        style={{
          backgroundColor: color + "18",
          color: color,
          border: `1px solid ${color}44`,
        }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
    );
  };

  return (
    <PageLayout
      title={t("Leads de Campanhas Pagas")}
      subtitle={t(
        "Visualize, gerencie e sincronize os leads das suas campanhas pagas.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-4 py-4"
      loading={loading}
      skeleton={<CampaignLeadsSkeleton viewMode={viewMode} />}
      actions={
        <div className="flex items-center gap-2">
          {/* Tags manager */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setTagManagerOpen(true)}
            icon={<Tag className={HEADER_SECONDARY_ICON} />}
            className={HEADER_SECONDARY_BUTTON}
          >
            <span className="text-xs">{t("Tags")}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAudienceModalOpen(true)}
            icon={<Target className={HEADER_SECONDARY_ICON} />}
            className={HEADER_SECONDARY_BUTTON}
          >
            <span className="hidden sm:inline text-xs">
              {t("Criar Público")}
            </span>
            <span className="sm:hidden text-xs">{t("Público")}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setUploadModalOpen(true)}
            icon={<Zap className={HEADER_SECONDARY_ICON} />}
            className={HEADER_SECONDARY_BUTTON}
          >
            <span className="text-xs">{t("Importar")}</span>
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setCreateModalOpen(true)}
            icon={<PlusCircle className={HEADER_ICON} />}
            className={HEADER_PRIMARY_BUTTON}
          >
            <span className="hidden sm:inline text-xs">{t("Novo Lead")}</span>
            <span className="sm:hidden text-xs">{t("Lead")}</span>
          </Button>
        </div>
      }
    >
      <div className="flex flex-col">
        {/* Toggle de colapso dos KPIs (Performance Overview + Qualidade & Saúde) */}
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            {t("Indicadores")}
          </h2>
          <button
            type="button"
            onClick={() => setKpisCollapsed((prev) => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 shadow-xs transition-all"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                kpisCollapsed && "-rotate-90",
              )}
            />
            {kpisCollapsed
              ? t("Mostrar Indicadores")
              : t("Ocultar Indicadores")}
          </button>
        </div>

        {!kpisCollapsed && (
          <>
            {/* Performance Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              {overviewStats.map((stat, i) => {
                const c = KPI_COLORS[stat.color];
                return (
                  <div
                    key={i}
                    className={cn(
                      "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 flex flex-col gap-3 overflow-hidden group hover:-translate-y-0.5",
                      c.border,
                      c.hoverGlow,
                    )}
                  >
                    {/* Colored top accent bar, exactly as in AdsList */}
                    <div
                      className={cn(
                        "absolute top-0 left-0 right-0 h-[3px]",
                        c.iconBg,
                      )}
                    />

                    <div className="flex items-start justify-between pt-0.5">
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">
                            {stat.label}
                          </p>
                          {stat.badge && (
                            <span
                              className={cn(
                                "text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase shrink-0",
                                c.subBg,
                                c.subText,
                              )}
                            >
                              {stat.badge}
                            </span>
                          )}
                        </div>
                        <h3
                          className={cn(
                            "text-2xl font-black tracking-tight leading-none tabular-nums",
                            c.text,
                          )}
                        >
                          {stat.val}
                        </h3>
                      </div>
                      <div
                        className={cn(
                          "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110",
                          c.iconBg,
                          c.iconText,
                        )}
                      >
                        <stat.icon className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quality & Health Section */}
            <div className="mb-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {qualityStats.map((stat, i) => {
                  const c = KPI_COLORS[stat.color];
                  return (
                    <div
                      key={i}
                      className={cn(
                        "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 flex flex-col gap-3 overflow-hidden group hover:-translate-y-0.5",
                        c.border,
                        c.hoverGlow,
                      )}
                    >
                      <div
                        className={cn(
                          "absolute top-0 left-0 right-0 h-[3px]",
                          c.iconBg,
                        )}
                      />
                      <div className="flex items-start justify-between pt-0.5">
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">
                              {stat.label}
                            </p>
                            {stat.badge && (
                              <span
                                className={cn(
                                  "text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase shrink-0",
                                  c.subBg,
                                  c.subText,
                                )}
                              >
                                {stat.badge}
                              </span>
                            )}
                          </div>
                          <h3
                            className={cn(
                              "text-2xl font-black tracking-tight leading-none tabular-nums",
                              c.text,
                            )}
                          >
                            {stat.val}
                          </h3>
                        </div>
                        <div
                          className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110",
                            c.iconBg,
                            c.iconText,
                          )}
                        >
                          <stat.icon className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Action Bar - full-bleed sticky filter bar, com duas linhas e toggle de view */}
        <div className="sticky top-0 z-30 -mx-4 px-4 py-2 mb-4 bg-white/95 backdrop-blur-md border-b border-purple-200 flex flex-col gap-2">
          {/* Linha 1: Busca, Visão e Ações — os filtros ficam todos juntos
              na Linha 2, para não competirem por espaço com a busca/ações
              e acabarem cortando texto. */}
          <div className="flex flex-row flex-wrap gap-2 items-center w-full">
            <div className="flex-1 min-w-[200px]">
              <Input
                type="text"
                placeholder={t("Buscar: Nome, e-mail, telefone, empresa...")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                inputSize="sm"
                icon={<Search className="h-4 w-4 text-gray-400" />}
                className="w-full h-8! sm:h-9!"
              />
            </div>

            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 border border-purple-200 shrink-0">
              <button
                onClick={() => switchView("list")}
                className={`flex items-center justify-center gap-1.5 px-3 py-1 sm:py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === "list" ? "bg-white text-purple-700 shadow-xs" : "text-gray-400 hover:text-gray-600"}`}
              >
                <LayoutList className="h-3.5 w-3.5" />{" "}
                <span className="hidden sm:inline">{t("Lista")}</span>
              </button>
              <button
                onClick={() => switchView("kanban")}
                className={`flex items-center justify-center gap-1.5 px-3 py-1 sm:py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === "kanban" ? "bg-white text-purple-700 shadow-xs" : "text-gray-400 hover:text-gray-600"}`}
              >
                <Columns className="h-3.5 w-3.5" />{" "}
                <span className="hidden sm:inline">Kanban</span>
              </button>
            </div>

            {viewMode === "kanban" && canEditStages && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => kanbanRef.current?.openStageManager()}
                icon={<Settings2 className="h-3.5 w-3.5" />}
                className="rounded-lg shrink-0 h-8! sm:h-9! min-h-[32px]! py-1! text-purple-600 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:text-purple-700"
              >
                <span className="hidden sm:inline text-xs font-black uppercase tracking-widest">
                  {t("Gerenciar Etapas")}
                </span>
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExportLeads}
              isLoading={exporting}
              icon={<FileSpreadsheet className="h-3.5 w-3.5" />}
              className="rounded-lg shrink-0 h-8! sm:h-9! min-h-[32px]! py-1!"
            >
              <span className="hidden sm:inline text-xs">
                {exporting ? t("Exportando...") : t("Exportar")}
              </span>
            </Button>
          </div>

          {/* Linha 2: todos os filtros juntos. Grid auto-fit com piso de
              180px por coluna — cada filtro ocupa a largura que precisa
              (nunca menos que 180px) e quebra de linha sozinho conforme o
              espaço disponível, em vez de larguras fixas escolhidas "no
              olho" que cortavam texto de labels mais longas (ex.: "Todos
              Status", "Sincronizado CRM", "Excluir duplicados"). */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2 w-full">
            <div className="w-full">
              <Select
                value={filters.status}
                onChange={(v) =>
                  setFilters({ ...filters, status: v as string })
                }
                showSelectedCount
                options={[
                  {
                    value: "",
                    label: t("Status"),
                    icon: <ListFilter />,
                    count: stats.total_leads,
                  },
                  {
                    value: "new",
                    label: t("Novo"),
                    icon: <Zap />,
                    count: stats.by_status?.new,
                  },
                  {
                    value: "contacted",
                    label: t("Contatado"),
                    icon: <PhoneCall />,
                    count: stats.by_status?.contacted,
                  },
                  {
                    value: "negotiating",
                    label: t("Negociando"),
                    icon: <TrendingUp />,
                    count: stats.by_status?.negotiating,
                  },
                  {
                    value: "visit_scheduled",
                    label: t("Visita Agendada"),
                    icon: <CalendarCheck />,
                    count: stats.by_status?.visit_scheduled,
                  },
                  {
                    value: "proposal_sent",
                    label: t("Proposta Enviada"),
                    icon: <FileText />,
                    count: stats.by_status?.proposal_sent,
                  },
                  {
                    value: "qualified",
                    label: t("Qualificado"),
                    icon: <CheckCircle2 />,
                    count: stats.by_status?.qualified,
                  },
                  {
                    value: "closed",
                    label: t("Fechado"),
                    icon: <CheckCircle />,
                    count: stats.by_status?.closed,
                  },
                  {
                    value: "lost",
                    label: t("Perdido"),
                    icon: <XCircle />,
                    count: stats.by_status?.lost,
                  },
                  {
                    value: "disqualified",
                    label: t("Desqualificado"),
                    icon: <Ban />,
                    count: stats.by_status?.disqualified,
                  },
                  {
                    value: "synced_to_crm",
                    label: t("Sincronizado CRM"),
                    icon: <RefreshCw />,
                    count: stats.by_status?.synced_to_crm,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>

            <div className="w-full">
              <Select
                value={filters.platform}
                onChange={(v) =>
                  setFilters({ ...filters, platform: v as string })
                }
                showSelectedCount
                options={[
                  {
                    value: "",
                    label: t("Plataformas"),
                    icon: <ListFilter />,
                    count: stats.total_leads,
                  },
                  {
                    value: "facebook",
                    label: "Facebook",
                    icon: (
                      <span className={getPlatformConfig("facebook").color}>
                        <PlatformIcon platform="facebook" />
                      </span>
                    ),
                    count: stats.by_platform?.facebook,
                  },
                  {
                    value: "instagram",
                    label: "Instagram",
                    icon: (
                      <span className={getPlatformConfig("instagram").color}>
                        <PlatformIcon platform="instagram" />
                      </span>
                    ),
                    count: stats.by_platform?.instagram,
                  },
                  {
                    value: "google_ads",
                    label: "Google Ads",
                    icon: (
                      <span className={getPlatformConfig("google_ads").color}>
                        <PlatformIcon platform="google_ads" />
                      </span>
                    ),
                    count: stats.by_platform?.google_ads,
                  },
                  {
                    value: "direct",
                    label: t("Site / Direto"),
                    icon: (
                      <span className={getPlatformConfig("direct").color}>
                        <PlatformIcon platform="direct" />
                      </span>
                    ),
                    count: stats.by_platform?.direct,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>

            <div className="w-full">
              <Select
                value={filters.qualification_status}
                onChange={(v) =>
                  setFilters({ ...filters, qualification_status: v as string })
                }
                showSelectedCount
                options={[
                  {
                    value: "",
                    label: t("Qualificações"),
                    icon: <ListFilter />,
                    count: stats.total_unique || stats.total_leads,
                  },
                  {
                    value: "qualified",
                    label: t("Qualificado"),
                    icon: <CheckCircle2 />,
                    count: stats.qualified_leads,
                  },
                  {
                    value: "disqualified",
                    label: t("Desqualificado"),
                    icon: <Ban />,
                    count: stats.disqualified_leads,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>

            <div className="w-full">
              <Select
                value={filters.is_duplicate}
                onChange={(v) =>
                  setFilters({ ...filters, is_duplicate: v as string })
                }
                showSelectedCount
                options={[
                  {
                    value: "",
                    label: t("Duplicados"),
                    icon: <ListFilter />,
                    count: stats.total_leads,
                  },
                  {
                    value: "true",
                    label: t("Apenas duplicados"),
                    icon: <Copy />,
                    count: stats.duplicate_leads,
                  },
                  {
                    value: "false",
                    label: t("Excluir duplicados"),
                    icon: <XCircle />,
                    count: stats.total_leads - stats.duplicate_leads,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>

            <div className="w-full">
              <Select
                value={filters.is_cancelled}
                onChange={(v) =>
                  setFilters({ ...filters, is_cancelled: v as string })
                }
                showSelectedCount
                options={[
                  {
                    value: "",
                    label: t("Cancelados"),
                    icon: <ListFilter />,
                    count: stats.total_leads,
                  },
                  {
                    value: "true",
                    label: t("Apenas cancelados"),
                    icon: <Ban />,
                    count: stats.cancelled_leads,
                  },
                  {
                    value: "false",
                    label: t("Excluir cancelados"),
                    icon: <XCircle />,
                    count: stats.total_leads - stats.cancelled_leads,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>

            <div className="w-full">
              <Select
                value={filters.show_hidden}
                onChange={(v) =>
                  setFilters({ ...filters, show_hidden: v as string })
                }
                options={[
                  { value: "", label: t("Sem ocultos"), icon: <ListFilter /> },
                  {
                    value: "true",
                    label: t("Mostrar ocultos"),
                    icon: <EyeOff />,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>

            <div className="w-full">
              <Select
                value={filters.is_fraud}
                onChange={(v) =>
                  setFilters({ ...filters, is_fraud: v as string })
                }
                showSelectedCount
                options={[
                  {
                    value: "",
                    label: t("Fraudes"),
                    icon: <ListFilter />,
                    count: stats.total_leads,
                  },
                  {
                    value: "true",
                    label: t("Apenas fraudes"),
                    icon: <AlertTriangle />,
                    count: stats.fraud_leads,
                  },
                  {
                    value: "false",
                    label: t("Excluir fraudes"),
                    icon: <XCircle />,
                    count: stats.total_leads - stats.fraud_leads,
                  },
                ]}
                size="sm"
                className="h-8! sm:h-9!"
              />
            </div>
          </div>

          {/* Resumo de filtros ativos — mesmo padrão da página Anúncios:
              mostra quantos leads estão sendo exibidos e permite remover
              cada filtro individualmente, sem precisar abrir cada select. */}
          {stats.total_leads > 0 &&
            (filters.status ||
              filters.qualification_status ||
              filters.platform ||
              filters.is_duplicate ||
              filters.is_cancelled ||
              filters.is_fraud ||
              filters.show_hidden ||
              filters.search) && (
              <div className="pt-2 border-t border-purple-100 flex gap-2 items-center justify-between text-xs text-gray-600 flex-wrap">
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    {t("Exibindo")}{" "}
                    <strong className="text-gray-900">{totalCount}</strong>{" "}
                    {t("de")} {stats.total_leads} {t("leads")}
                  </span>

                  <div className="w-px h-3 bg-gray-300 hidden sm:block mx-1" />

                  {filters.search && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Busca")}: {filters.search}
                      <button
                        onClick={() => {
                          setSearchInput("");
                          setFilters({ ...filters, search: "" });
                        }}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.status && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Status")}:{" "}
                      {
                        {
                          new: t("Novo"),
                          contacted: t("Contatado"),
                          negotiating: t("Negociando"),
                          qualified: t("Qualificado"),
                          closed: t("Fechado"),
                          lost: t("Perdido"),
                          disqualified: t("Desqualificado"),
                          synced_to_crm: t("Sincronizado CRM"),
                        }[filters.status]
                      }
                      <button
                        onClick={() => setFilters({ ...filters, status: "" })}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.qualification_status && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Qualificação")}:{" "}
                      {filters.qualification_status === "qualified"
                        ? t("Qualificado")
                        : t("Desqualificado")}
                      <button
                        onClick={() =>
                          setFilters({ ...filters, qualification_status: "" })
                        }
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.platform && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Plataforma")}: {getPlatformName(filters.platform)}
                      <button
                        onClick={() => setFilters({ ...filters, platform: "" })}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.is_duplicate && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {filters.is_duplicate === "true"
                        ? t("Apenas duplicados")
                        : t("Excluir duplicados")}
                      <button
                        onClick={() =>
                          setFilters({ ...filters, is_duplicate: "" })
                        }
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.is_cancelled && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {filters.is_cancelled === "true"
                        ? t("Apenas cancelados")
                        : t("Excluir cancelados")}
                      <button
                        onClick={() =>
                          setFilters({ ...filters, is_cancelled: "" })
                        }
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filters.is_fraud && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {filters.is_fraud === "true"
                        ? t("Apenas fraudes")
                        : t("Excluir fraudes")}
                      <button
                        onClick={() => setFilters({ ...filters, is_fraud: "" })}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>

                <button
                  onClick={() => {
                    setSearchInput("");
                    setFilters({
                      status: "",
                      qualification_status: "",
                      platform: "",
                      campaign_id: filters.campaign_id,
                      is_duplicate: "",
                      is_cancelled: "",
                      is_fraud: "",
                      show_hidden: "",
                      search: "",
                    });
                  }}
                  className="text-purple-600 hover:text-purple-700 font-black uppercase tracking-tighter shrink-0"
                >
                  {t("Limpar filtros")}
                </button>
              </div>
            )}
        </div>

        {/* Kanban view */}
        {viewMode === "kanban" && (
          <LeadKanban
            ref={kanbanRef}
            key={kanbanRefreshKey}
            availableTags={availableTags}
            onTagsChange={loadLeads}
            filters={filters}
            onCanEditStagesChange={setCanEditStages}
          />
        )}

        {/* Lista de leads */}
        {viewMode === "list" && (
          <div className="space-y-2">
            {/* Bulk Actions Bar */}
            {selectedLeads.length > 0 && (
              <div className="flex items-center justify-between bg-red-50 border border-red-100 px-4 py-2 rounded-xl animate-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-black text-red-600 uppercase tracking-widest">
                    {t("{count} selecionados", { count: selectedLeads.length })}
                  </span>
                  <div className="w-px h-4 bg-red-200" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDelete}
                    isLoading={deletingBulk}
                    icon={<Trash2 className="h-3.5 w-3.5" />}
                    className="bg-white border-red-200 text-red-600 hover:bg-red-600 hover:text-white transition-all text-[9px] font-black uppercase tracking-widest h-8"
                  >
                    {t("Excluir Permanentemente")}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelectedLeads([])}
                  className="bg-white! border! border-red-100! shadow-none! rounded-lg text-gray-400! hover:text-red-600! hover:bg-white! text-[10px] font-bold uppercase tracking-widest"
                >
                  {t("Cancelar")}
                </Button>
              </div>
            )}

            <div className="bg-white border border-purple-200 rounded-xl shadow-xs">
              <div className="overflow-x-auto overflow-y-visible">
                <table className="w-full">
                  <thead className="bg-gray-50/50 border-b border-purple-200">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded-sm border-purple-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                          checked={
                            leads.length > 0 &&
                            selectedLeads.length === leads.length
                          }
                          onChange={(e) => {
                            if (e.target.checked)
                              setSelectedLeads(leads.map((l) => l.id));
                            else setSelectedLeads([]);
                          }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                        {t("Lead & Perfil")}
                      </th>
                      <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                        {t("Origem / Campanha")}
                      </th>
                      <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                        {t("Status & Qualificação")}
                      </th>
                      <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap text-right">
                        {t("Captura")}
                      </th>
                      <th className="px-4 py-3 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                        {t("Gestão")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-100">
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <EmptyState
                            icon={<Users className="h-12 w-12" />}
                            title={t("O horizonte de leads está vazio")}
                            description={t(
                              "Aguarde novas conversões ou ajuste seus filtros.",
                            )}
                            size="sm"
                          />
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr
                          key={lead.id}
                          className={cn(
                            "hover:bg-purple-50/40 transition-colors group cursor-pointer border-b border-purple-100",
                            selectedLeads.includes(lead.id) && "bg-purple-50",
                          )}
                          onClick={() => setViewingLead(lead)}
                        >
                          <td
                            className="px-4 py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded-sm border-purple-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                              checked={selectedLeads.includes(lead.id)}
                              onChange={() => {
                                if (selectedLeads.includes(lead.id))
                                  setSelectedLeads((prev) =>
                                    prev.filter((id) => id !== lead.id),
                                  );
                                else
                                  setSelectedLeads((prev) => [
                                    ...prev,
                                    lead.id,
                                  ]);
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-linear-to-br from-purple-100 to-indigo-50 flex items-center justify-center text-purple-600 font-black shrink-0 border border-purple-200 group-hover:scale-110 transition-transform shadow-xs">
                                {(lead.name || "L").charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-[180px]">
                                <div className="font-black text-gray-900 text-sm underline decoration-purple-100 underline-offset-4 decoration-2">
                                  {lead.name || t("Sem nome")}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5 overflow-hidden">
                                  <div className="text-[10px] font-bold text-gray-400 lowercase tracking-tight truncate flex-1 min-w-0">
                                    {lead.email}
                                  </div>
                                  {lead.phone && (
                                    <div className="text-[10px] font-black text-indigo-400 shrink-0 select-all tracking-tighter">
                                      • {lead.phone}
                                    </div>
                                  )}
                                </div>
                                {(lead.company || lead.job_title) && (
                                  <div className="text-[9px] font-black text-purple-400 uppercase tracking-widest mt-1 opacity-70 flex items-center gap-1.5 overflow-hidden">
                                    <Building2 className="w-3 h-3 shrink-0" />
                                    <span className="truncate">
                                      {lead.job_title
                                        ? `${lead.job_title} @ `
                                        : ""}
                                      {lead.company || ""}
                                    </span>
                                  </div>
                                )}
                                {(lead.tags?.length ?? 0) > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {(lead.tags || []).map((tagId) => {
                                      const tag = availableTags.find(
                                        (t) => t.id === tagId,
                                      );
                                      if (!tag) return null;
                                      return (
                                        <span
                                          key={tagId}
                                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-black"
                                          style={{
                                            backgroundColor: tag.color + "20",
                                            color: tag.color,
                                            border: `1px solid ${tag.color}44`,
                                          }}
                                        >
                                          {tag.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="text-[8px] h-4 bg-white border-purple-200 text-purple-700 font-black"
                                >
                                  {lead.platform
                                    ? getPlatformName(lead.platform)
                                    : "OFFLINE"}
                                </Badge>
                                {lead.is_duplicate && (
                                  <div
                                    title={t("Duplicado")}
                                    className="p-0.5 bg-amber-50 rounded-md border border-amber-200"
                                  >
                                    <Copy className="h-2.5 w-2.5 text-amber-500" />
                                  </div>
                                )}
                              </div>
                              <div className="text-[10px] font-black text-gray-600 truncate max-w-[140px] uppercase leading-tight">
                                {lead.campaign?.name || t("Manual / Orgânico")}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                {getStatusBadge(lead)}
                                <div
                                  title={t("Sync CRM")}
                                  className={cn(
                                    "w-1.5 h-1.5 rounded-full",
                                    lead.status === "synced_to_crm"
                                      ? "bg-green-500"
                                      : "bg-gray-300",
                                  )}
                                />
                              </div>
                              {typeof lead.lead_score === "number" &&
                                !Number.isNaN(lead.lead_score) && (
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-purple-500"
                                        style={{
                                          width: `${Math.min(100, (lead.lead_score || 0) * 10)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-[9px] font-black text-purple-400">
                                      {lead.lead_score}%
                                    </span>
                                  </div>
                                )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="text-xs font-black text-gray-900 tracking-tighter">
                              {new Date(lead.created_at).toLocaleDateString(
                                "pt-BR",
                              )}
                            </div>
                            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                              {new Date(lead.created_at).toLocaleTimeString(
                                "pt-BR",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </div>
                          </td>
                          <td
                            className="px-4 py-3 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-2">
                              <ActionDropdown
                                label=""
                                size="sm"
                                loading={
                                  syncing === lead.id ||
                                  rowActionLoading === lead.id
                                }
                                actions={[
                                  {
                                    label: t("Ver Dossiê"),
                                    icon: Eye,
                                    onClick: () => setViewingLead(lead),
                                  },
                                  {
                                    label: t("Atribuir Tags"),
                                    icon: Tag,
                                    onClick: () => handleOpenTagPicker(lead),
                                    variant: "default",
                                  },
                                  {
                                    label: t("Sincronizar CAPI"),
                                    icon: Zap,
                                    onClick: () => handleSyncCapi(lead.id),
                                    variant: "default",
                                    loading: rowActionLoading === lead.id,
                                  },
                                  {
                                    label:
                                      lead.status === "synced_to_crm"
                                        ? t("Re-Sincronizar CRM")
                                        : t("Sincronizar com CRM"),
                                    icon: RefreshCw,
                                    onClick: () => handleSyncToCRM(lead.id),
                                    variant: "default",
                                    loading: syncing === lead.id,
                                  },
                                  {
                                    label: lead.is_duplicate
                                      ? t("Desmarcar Duplicado")
                                      : t("Marcar como Duplicado"),
                                    icon: Copy,
                                    onClick: () =>
                                      handleMarkDuplicate(
                                        lead.id,
                                        !lead.is_duplicate,
                                      ),
                                    variant: "default",
                                    loading: rowActionLoading === lead.id,
                                  },
                                  {
                                    label: lead.is_fraud
                                      ? t("Remover Flag")
                                      : t("Reportar Fraude"),
                                    icon: AlertTriangle,
                                    onClick: () =>
                                      handleMarkFraud(lead.id, !lead.is_fraud),
                                    variant: "default",
                                    loading: rowActionLoading === lead.id,
                                  },
                                  {
                                    label: lead.is_hidden
                                      ? t("Mostrar de novo")
                                      : t("Ocultar Lead"),
                                    icon: lead.is_hidden ? Eye : EyeOff,
                                    onClick: () =>
                                      hideLeads([lead.id], !lead.is_hidden),
                                    variant: "default",
                                  },
                                  {
                                    label: t("Excluir Lead"),
                                    icon: Trash2,
                                    onClick: () => handleDeleteLead(lead.id),
                                    variant: "danger",
                                    divider: true,
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação e Info */}
              {totalCount > 0 && (
                <div className="px-4 py-3 border-t border-purple-100 bg-white/50">
                  <Pagination
                    currentPage={page}
                    totalPages={Math.ceil(totalCount / pageSize)}
                    onPageChange={setPage}
                    totalItems={totalCount}
                    itemsPerPage={pageSize}
                    showInfo={true}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal: Tags */}
        <TagManagerModal
          open={tagManagerOpen}
          onClose={() => setTagManagerOpen(false)}
          onTagsChange={setAvailableTags}
        />

        {/* Modal: Upload planilha para CAPI */}
        <Modal
          open={uploadModalOpen}
          onClose={closeUploadModal}
          title={t("Sincronização de Audience (CAPI)")}
          subtitle={t(
            "Importe sua base de dados externa para nutrir o ecossistema CAPI (Conversion API) e otimizar públicos.",
          )}
          icon={<Upload className="h-5 w-5" />}
          footer={
            <div className="flex flex-row items-center justify-end gap-3 w-full p-1">
              <Button
                type="button"
                variant="secondary"
                onClick={closeUploadModal}
                className="bg-white! border! border-purple-100! shadow-none! rounded-xl h-10 px-6 text-[10px] font-black uppercase tracking-widest text-gray-400! hover:text-red-600! hover:bg-white! transition-all"
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleUploadExcel}
                disabled={!uploadFile || uploading}
                isLoading={uploading}
                icon={<Zap className="h-4 w-4" />}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-8 text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-500/20 active:scale-95 transition-all"
              >
                {uploading ? t("Processando...") : t("Iniciar Importação")}
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            {/* Header: Download Template */}
            <div className="group relative overflow-hidden rounded-xl bg-linear-to-br from-purple-50 to-indigo-50 p-4 border border-purple-100/50 shadow-xs">
              <div className="flex items-center justify-between gap-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-purple-600 shadow-xs border border-purple-100">
                    <FileSpreadsheet className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-tight">
                        {t("Planilha de Exemplo")}
                      </h3>
                      <span className="bg-purple-600 text-[8px] text-white px-1.5 py-0.5 rounded-full font-black uppercase tracking-tighter">
                        {t("Obrigatório")}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 font-bold leading-tight">
                      {t(
                        "Use este modelo para garantir a importação sem erros.",
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  icon={
                    downloadingTemplate ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )
                  }
                  className="bg-white border-purple-200 hover:bg-purple-50 text-purple-700 hover:text-purple-800 h-9 rounded-xl shadow-xs hover:shadow-md transition-all text-[9px] font-black uppercase tracking-widest px-4 border"
                >
                  {downloadingTemplate ? t("Baixando...") : t("Download")}
                </Button>
              </div>
            </div>

            {/* File Dropzone Custom */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Fonte de Dados (.xlsx)")}
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "relative group cursor-pointer rounded-xl border-2 border-dashed transition-all duration-300 p-8 flex flex-col items-center justify-center gap-3",
                  uploadFile
                    ? "bg-emerald-50/30 border-emerald-200"
                    : "bg-gray-50/50 border-purple-100 hover:border-purple-300 hover:bg-white hover:shadow-xl hover:shadow-purple-500/5",
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setUploadFile(f || null);
                    setUploadResult(null);
                  }}
                />

                <div
                  className={cn(
                    "w-14 h-14 rounded-xl flex items-center justify-center border transition-all duration-500",
                    uploadFile
                      ? "bg-emerald-100 text-emerald-600 border-emerald-200 scale-110"
                      : "bg-white text-purple-600 border-purple-100 group-hover:scale-110",
                  )}
                >
                  {uploadFile ? (
                    <CheckCircle2 className="h-7 w-7" />
                  ) : (
                    <Upload className="h-7 w-7" />
                  )}
                </div>

                <div className="text-center">
                  {uploadFile ? (
                    <>
                      <p className="text-sm font-black text-emerald-900 tracking-tight">
                        {uploadFile.name}
                      </p>
                      <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest mt-1">
                        {(uploadFile.size / 1024).toFixed(1)} KB ·{" "}
                        {t("Pronto para envio")}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-black text-gray-900 tracking-tight">
                        {t("Selecione ou Arraste o Arquivo")}
                      </p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                        {t("Suporta apenas planilhas Excel estruturadas")}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Options Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  {t("Destino CAPI")}
                </label>
                <Select
                  value={uploadPlatform}
                  onChange={(v) => setUploadPlatform(v)}
                  options={[
                    { value: "facebook", label: "Facebook (Meta)" },
                    { value: "instagram", label: "Instagram (Meta)" },
                    { value: "google_ads", label: "Google Ads" },
                  ]}
                  size="sm"
                  className="bg-gray-50/50"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  {t("Métrica de Validação")}
                </label>
                <Select
                  value={uploadQualification}
                  onChange={(v) => setUploadQualification(v)}
                  options={[
                    {
                      value: "qualified",
                      label: t("Lead Qualificado (Lookalike)"),
                    },
                    { value: "not_qualified", label: t("Inbound Comum") },
                  ]}
                  size="sm"
                  className="bg-gray-50/50"
                />
              </div>
            </div>

            {/* Custom Audience Section */}
            <div className="space-y-3 bg-purple-50/50 p-4 rounded-xl border border-purple-100/50 mt-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="create_ca"
                  checked={createCustomAudience}
                  onChange={(e) => setCreateCustomAudience(e.target.checked)}
                  className="rounded-sm border-purple-300 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor="create_ca"
                  className="text-xs font-bold text-gray-700"
                >
                  {t("Criar Público Personalizado na Meta Ads")}
                </label>
              </div>
              {createCustomAudience && (
                <div className="space-y-1.5 pl-5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    {t("Nome do Público")}
                  </label>
                  <input
                    type="text"
                    value={customAudienceName}
                    onChange={(e) => setCustomAudienceName(e.target.value)}
                    placeholder={t("Ex: Custom Audience - Clientes Virtus")}
                    className="w-full text-sm rounded-lg border border-purple-100 px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              )}
            </div>

            {/* Result Feedback */}
            {uploadResult && (
              <div className="animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="rounded-xl border border-purple-100 bg-white shadow-xs p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1.5 h-4 bg-purple-600 rounded-full" />
                    <h4 className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                      {t("Resultado do Processamento")}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100/50">
                      <div className="text-lg font-black text-emerald-900 leading-none">
                        {uploadResult.imported}
                      </div>
                      <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest mt-1">
                        {t("Importados")}
                      </div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-100/50">
                      <div className="text-lg font-black text-amber-900 leading-none">
                        {uploadResult.duplicates_skipped}
                      </div>
                      <div className="text-[9px] font-bold text-amber-600 uppercase tracking-widest mt-1">
                        {t("Duplicados")}
                      </div>
                    </div>
                  </div>
                  {uploadResult.audience_id && (
                    <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100 mb-4">
                      <div className="text-[10px] font-bold text-blue-700 uppercase tracking-widest">
                        {t("Custom Audience Criada")}
                      </div>
                      <div className="text-sm font-medium text-blue-900 mt-1">
                        ID: {uploadResult.audience_id}
                      </div>
                    </div>
                  )}
                  {uploadResult.audience_error && (
                    <div className="bg-red-50/50 rounded-xl p-3 border border-red-100 mb-4">
                      <div className="text-[10px] font-bold text-red-700 uppercase tracking-widest">
                        {t("Erro na Criação do Público")}
                      </div>
                      <div className="text-xs font-medium text-red-900 mt-1">
                        {uploadResult.audience_error}
                      </div>
                      {uploadResult.audience_error.includes("Termos") && (
                        <div className="mt-3 flex gap-2">
                          <a
                            href="https://business.facebook.com/ads/manage/customaudiences/tos/"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-[9px] font-black uppercase tracking-wider bg-blue-600 text-white px-3 py-1.5 rounded-sm hover:bg-blue-700 shadow-xs transition-colors"
                          >
                            {t("Termos Gerais")}
                          </a>
                          <a
                            href="https://business.facebook.com/customaudiences/app/tos/"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-[9px] font-black uppercase tracking-wider bg-indigo-600 text-white px-3 py-1.5 rounded-sm hover:bg-indigo-700 shadow-xs transition-colors"
                          >
                            {t("Termos de Arquivo")}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  {uploadResult.errors.length > 0 && (
                    <div className="bg-red-50/50 rounded-xl p-3 border border-red-100">
                      <div className="flex items-center gap-2 text-red-700 mb-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          {t("Inconsistências Detectadas ({count})", {
                            count: uploadResult.errors.length,
                          })}
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {uploadResult.errors.slice(0, 2).map((e, idx) => (
                          <li
                            key={idx}
                            className="text-[10px] text-red-600/80 font-medium"
                          >
                            •{" "}
                            {t("Linha {row}: {message}", {
                              row: e.row,
                              message: e.message,
                            })}
                          </li>
                        ))}
                        {uploadResult.errors.length > 2 && (
                          <li className="text-[9px] text-red-400">
                            {t("...e mais {count} erros", {
                              count: uploadResult.errors.length - 2,
                            })}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>

        {/* Modal: Novo lead manual */}
        <Modal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          title={t("Novo Lead Manual")}
          subtitle={t("Cadastre um lead manualmente. O email é obrigatório.")}
          icon={<PlusCircle className="h-5 w-5" />}
          footer={
            <div className="flex flex-row items-center justify-end gap-3 w-full p-1">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCreateModalOpen(false)}
                className="bg-white! border! border-purple-100! shadow-none! rounded-xl h-10 px-6 text-[10px] font-black uppercase tracking-widest text-gray-400! hover:text-red-600! hover:bg-white! transition-all"
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleCreateLead}
                disabled={creating}
                isLoading={creating}
                icon={<PlusCircle className="h-4 w-4" />}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-10 px-8 text-[10px] font-black uppercase tracking-widest shadow-xl shadow-emerald-500/20 active:scale-95 transition-all"
              >
                {creating ? t("Salvando...") : t("Salvar Lead")}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            {/* Nome */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Nome")}
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({ ...createForm, name: e.target.value })
                }
                placeholder={t("Nome completo")}
                className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
              />
            </div>

            {/* Emails */}
            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                  {t("Email")} <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setCreateForm({
                      ...createForm,
                      additional_emails: [...createForm.additional_emails, ""],
                    })
                  }
                  className="flex items-center gap-1 text-[9px] font-black text-purple-600 hover:text-purple-800 uppercase tracking-widest transition-colors"
                >
                  <PlusCircle className="h-3 w-3" /> {t("Adicionar email")}
                </button>
              </div>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) =>
                  setCreateForm({ ...createForm, email: e.target.value })
                }
                placeholder="email@exemplo.com"
                className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
              />
              {createForm.additional_emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="email"
                    value={em}
                    onChange={(e) => {
                      const updated = [...createForm.additional_emails];
                      updated[i] = e.target.value;
                      setCreateForm({
                        ...createForm,
                        additional_emails: updated,
                      });
                    }}
                    placeholder={`${t("Email alternativo")} ${i + 2}`}
                    className="flex-1 h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setCreateForm({
                        ...createForm,
                        additional_emails: createForm.additional_emails.filter(
                          (_, j) => j !== i,
                        ),
                      })
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Telefones */}
            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                  {t("Telefone")}
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setCreateForm({
                      ...createForm,
                      additional_phones: [...createForm.additional_phones, ""],
                    })
                  }
                  className="flex items-center gap-1 text-[9px] font-black text-purple-600 hover:text-purple-800 uppercase tracking-widest transition-colors"
                >
                  <PlusCircle className="h-3 w-3" /> {t("Adicionar telefone")}
                </button>
              </div>
              <input
                type="tel"
                value={createForm.phone}
                onChange={(e) =>
                  setCreateForm({ ...createForm, phone: e.target.value })
                }
                placeholder="+55 11 99999-9999"
                className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
              />
              {createForm.additional_phones.map((ph, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="tel"
                    value={ph}
                    onChange={(e) => {
                      const updated = [...createForm.additional_phones];
                      updated[i] = e.target.value;
                      setCreateForm({
                        ...createForm,
                        additional_phones: updated,
                      });
                    }}
                    placeholder={`${t("Telefone alternativo")} ${i + 2}`}
                    className="flex-1 h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setCreateForm({
                        ...createForm,
                        additional_phones: createForm.additional_phones.filter(
                          (_, j) => j !== i,
                        ),
                      })
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Cargo */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Cargo")}
              </label>
              <input
                type="text"
                value={createForm.job_title}
                onChange={(e) =>
                  setCreateForm({ ...createForm, job_title: e.target.value })
                }
                placeholder={t("Ex: Gerente de Marketing")}
                className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
              />
            </div>

            {/* Empresa */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Empresa")}
              </label>
              <input
                type="text"
                value={createForm.company}
                onChange={(e) =>
                  setCreateForm({ ...createForm, company: e.target.value })
                }
                placeholder={t("Nome da empresa")}
                className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
              />
            </div>

            {/* Origem */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Origem")}
              </label>
              <Select
                value={createForm.platform}
                onChange={(v) =>
                  setCreateForm({
                    ...createForm,
                    platform: v,
                    origin_description: "",
                  })
                }
                options={[
                  { value: "facebook", label: "Facebook" },
                  { value: "instagram", label: "Instagram" },
                  { value: "both", label: "Meta (Facebook + Instagram)" },
                  { value: "google_ads", label: "Google Ads" },
                  { value: "linkedin", label: "LinkedIn" },
                  { value: "tiktok", label: "TikTok" },
                  { value: "whatsapp", label: "WhatsApp" },
                  { value: "referral", label: t("Indicação") },
                  { value: "organic", label: t("Orgânico / Site") },
                  { value: "event", label: t("Evento / Feira") },
                  { value: "other", label: t("Outros") },
                ]}
                size="sm"
                className="bg-gray-50/50"
              />
              {createForm.platform === "other" && (
                <input
                  type="text"
                  value={createForm.origin_description}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      origin_description: e.target.value,
                    })
                  }
                  placeholder={t("Descreva a origem do lead...")}
                  className="w-full h-10 px-4 rounded-lg border border-purple-200 bg-white text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
                />
              )}
            </div>

            {/* Como o lead chegou: mesma lista do Virtus Chat; no Leadflow o lead entra como offline com ela */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Como o lead chegou")} *
              </label>
              <Select
                value={createForm.offline_source}
                onChange={(v) =>
                  setCreateForm({ ...createForm, offline_source: v })
                }
                options={[
                  { value: "", label: t("Selecione...") },
                  ...OFFLINE_LEAD_SOURCES.map((o) => ({
                    value: o.value,
                    label: t(o.label),
                  })),
                ]}
                size="sm"
                className="bg-gray-50/50"
              />
            </div>

            {/* Etapa no Funil + Interesse */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  {t("Etapa no Funil")}
                </label>
                <Select
                  value={createForm.funnel_status}
                  onChange={(v) =>
                    setCreateForm({ ...createForm, funnel_status: v })
                  }
                  options={
                    kanbanStages.length > 0
                      ? kanbanStages.map((s) => ({
                          value: s.key,
                          label: s.label,
                        }))
                      : [
                          { value: "new", label: t("Novo") },
                          { value: "contacted", label: t("Contatado") },
                          { value: "negotiating", label: t("Negociando") },
                          {
                            value: "visit_scheduled",
                            label: t("Visita Agendada"),
                          },
                          {
                            value: "proposal_sent",
                            label: t("Proposta Enviada"),
                          },
                          { value: "qualified", label: t("Qualificado") },
                          { value: "closed", label: t("Fechado") },
                          { value: "lost", label: t("Perdido") },
                        ]
                  }
                  size="sm"
                  className="bg-gray-50/50"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  {t("Interesse / Produto")}
                </label>
                <input
                  type="text"
                  value={createForm.interest}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, interest: e.target.value })
                  }
                  placeholder={t("Ex: Plano premium, Consultoria...")}
                  className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
                />
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Observações")}
              </label>
              <textarea
                value={createForm.notes}
                onChange={(e) =>
                  setCreateForm({ ...createForm, notes: e.target.value })
                }
                placeholder={t("Contexto adicional, histórico da conversa...")}
                rows={3}
                className="w-full px-4 py-2.5 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all resize-none"
              />
            </div>
          </div>
        </Modal>

        {/* Modal: Atribuir Tags */}
        <Modal
          open={!!tagPickerLead}
          onClose={() => setTagPickerLead(null)}
          title={t("Atribuir Tags")}
          subtitle={tagPickerLead?.name || tagPickerLead?.email || ""}
          icon={<Tag className="h-5 w-5" />}
          footer={
            <div className="flex flex-row items-center justify-end gap-3 w-full p-1">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setTagPickerLead(null)}
                className="bg-white! border! border-purple-100! shadow-none! rounded-xl h-10 px-6 text-[10px] font-black uppercase tracking-widest text-gray-400! hover:text-red-600! hover:bg-white! transition-all"
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleSaveLeadTags}
                disabled={savingTags}
                isLoading={savingTags}
                icon={<Tag className="h-4 w-4" />}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-10 px-8 text-[10px] font-black uppercase tracking-widest shadow-xl shadow-purple-500/20 active:scale-95 transition-all"
              >
                {savingTags ? t("Salvando...") : t("Salvar Tags")}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            {availableTags.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <div className="w-12 h-12 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-center mx-auto">
                  <Tag className="h-5 w-5 text-purple-400" />
                </div>
                <p className="text-sm font-semibold text-gray-500">
                  {t("Nenhuma tag configurada.")}
                </p>
                <p className="text-[11px] text-gray-400">
                  {t(
                    "Crie tags no gerenciador de tags (botão Tags no topo da página).",
                  )}
                </p>
              </div>
            ) : (
              <>
                <p className="text-[11px] text-gray-400">
                  {t(
                    "Clique nas tags para selecionar ou remover. Salve para confirmar.",
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => {
                    const selected = pendingTags.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() =>
                          setPendingTags((prev) =>
                            prev.includes(tag.id)
                              ? prev.filter((id) => id !== tag.id)
                              : [...prev, tag.id],
                          )
                        }
                        className="flex items-center gap-2 px-3 py-2 rounded-full text-[11px] font-black transition-all border-2"
                        style={{
                          backgroundColor: selected
                            ? tag.color + "20"
                            : "transparent",
                          borderColor: selected ? tag.color : tag.color + "44",
                          color: tag.color,
                          boxShadow: selected
                            ? `0 0 0 3px ${tag.color}18`
                            : "none",
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                        {selected && (
                          <CheckCircle2 className="h-3 w-3 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {pendingTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPendingTags([])}
                    className="text-[10px] font-black text-red-400 hover:text-red-600 uppercase tracking-widest transition-colors"
                  >
                    {t("Remover todas as tags")}
                  </button>
                )}
              </>
            )}
          </div>
        </Modal>

        {/* Modal: Criar Público na Meta */}
        <Modal
          open={audienceModalOpen}
          onClose={() => setAudienceModalOpen(false)}
          title={t("Criar Público na Meta")}
          subtitle={t(
            "Gere um Custom Audience no Meta Ads com leads do seu CRM.",
          )}
          icon={<Target className="h-5 w-5" />}
          footer={
            <div className="flex flex-row items-center justify-end gap-3 w-full p-1">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAudienceModalOpen(false)}
                className="bg-white! border! border-purple-100! shadow-none! rounded-xl h-10 px-6 text-[10px] font-black uppercase tracking-widest text-gray-400! hover:text-red-600! hover:bg-white! transition-all"
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleCreateAudience}
                disabled={
                  creatingAudience || !audienceForm.audience_name.trim()
                }
                isLoading={creatingAudience}
                icon={<Target className="h-4 w-4" />}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-10 px-8 text-[10px] font-black uppercase tracking-widest shadow-xl shadow-blue-500/20 active:scale-95 transition-all disabled:opacity-50"
              >
                {creatingAudience ? t("Criando...") : t("Criar Público")}
              </Button>
            </div>
          }
        >
          <div className="space-y-5">
            {/* Nome do público */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                {t("Nome do Público")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={audienceForm.audience_name}
                onChange={(e) =>
                  setAudienceForm({
                    ...audienceForm,
                    audience_name: e.target.value,
                  })
                }
                placeholder={t("Ex: Vendas — Julho 2026")}
                className="w-full h-10 px-4 rounded-lg border border-purple-100 bg-gray-50/50 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all"
              />
            </div>

            {/* Leads selecionados — toggle rápido se houver seleção ativa */}
            {selectedLeads.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  setAudienceForm({
                    ...audienceForm,
                    use_selected: !audienceForm.use_selected,
                  })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                  audienceForm.use_selected
                    ? "border-blue-500 bg-blue-50"
                    : "border-purple-100 bg-gray-50/50 hover:border-blue-300"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-sm border-2 shrink-0 flex items-center justify-center transition-all ${audienceForm.use_selected ? "border-blue-500 bg-blue-500" : "border-gray-300"}`}
                >
                  {audienceForm.use_selected && (
                    <CheckCircle2 className="h-3 w-3 text-white" />
                  )}
                </div>
                <div>
                  <span className="text-[11px] font-black uppercase tracking-widest text-blue-700">
                    {t("Usar apenas leads selecionados")}
                  </span>
                  <span className="text-[10px] text-gray-500 ml-2">
                    ({t("{count} marcados", { count: selectedLeads.length })})
                  </span>
                </div>
              </button>
            )}

            {/* Filtros combinados — desabilitados se use_selected estiver ativo */}
            <fieldset
              disabled={audienceForm.use_selected}
              className={`space-y-4 transition-opacity ${audienceForm.use_selected ? "opacity-40 pointer-events-none" : ""}`}
            >
              {/* Qualificação */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  {t("Qualificação")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {(["", "qualified", "disqualified"] as const).map((val) => {
                    const labels: Record<string, string> = {
                      "": t("Todos"),
                      qualified: t("Qualificados"),
                      disqualified: t("Desqualificados"),
                    };
                    const counts: Record<string, number> = {
                      "": stats.total_unique || stats.total_leads,
                      qualified: stats.qualified_leads,
                      disqualified: stats.disqualified_leads,
                    };
                    const active = audienceForm.qualification_status === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() =>
                          setAudienceForm({
                            ...audienceForm,
                            qualification_status: val,
                          })
                        }
                        className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all border-2 ${
                          active
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-purple-100 bg-gray-50 text-gray-500 hover:border-blue-300"
                        }`}
                      >
                        {labels[val]}
                        <span
                          className={`ml-1.5 ${active ? "opacity-80" : "text-gray-400"}`}
                        >
                          ({counts[val]})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Etapas do funil */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  {t("Etapa no funil")}{" "}
                  <span className="text-gray-300 font-normal normal-case tracking-normal">
                    {t("(opcional — sem filtro = todas as etapas)")}
                  </span>
                </label>
                {chatPipelines.length > 0 && (
                  <select
                    aria-label={t("Funil")}
                    value={audienceForm.pipeline_id}
                    onChange={(e) =>
                      setAudienceForm({
                        ...audienceForm,
                        pipeline_id: e.target.value,
                        deal_columns: [],
                        statuses: [],
                      })
                    }
                    className="h-9 px-3 text-[11px] font-bold rounded-xl border border-purple-200 bg-white focus:outline-hidden focus:ring-2 focus:ring-purple-300"
                  >
                    <option value="">{t("Kanban do SMM")}</option>
                    {chatPipelines.map((p) => (
                      <option key={p.id} value={p.id}>
                        {t("Virtus Chat: {name}", { name: p.name })}
                      </option>
                    ))}
                  </select>
                )}
                <div className="flex flex-wrap gap-2">
                  {audienceStageOptions.map(({ key, label, color }) => {
                    const selected = audienceForm.pipeline_id
                      ? audienceForm.deal_columns
                      : audienceForm.statuses;
                    const active = selected.includes(key);
                    const next = active
                      ? selected.filter((s) => s !== key)
                      : [...selected, key];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setAudienceForm(
                            audienceForm.pipeline_id
                              ? { ...audienceForm, deal_columns: next }
                              : { ...audienceForm, statuses: next },
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black transition-all border-2"
                        style={{
                          backgroundColor: active
                            ? color + "20"
                            : "transparent",
                          borderColor: active ? color : color + "44",
                          color: active ? color : "#9ca3af",
                          boxShadow: active ? `0 0 0 2px ${color}18` : "none",
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        {label}
                        {active && <CheckCircle2 className="h-3 w-3" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              {availableTags.length > 0 && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                    {t("Tags")}{" "}
                    <span className="text-gray-300 font-normal normal-case tracking-normal">
                      {t("(opcional — sem filtro = qualquer tag)")}
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const active = audienceForm.tag_ids.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() =>
                            setAudienceForm({
                              ...audienceForm,
                              tag_ids: active
                                ? audienceForm.tag_ids.filter(
                                    (id) => id !== tag.id,
                                  )
                                : [...audienceForm.tag_ids, tag.id],
                            })
                          }
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black transition-all border-2"
                          style={{
                            backgroundColor: active
                              ? tag.color + "20"
                              : "transparent",
                            borderColor: active ? tag.color : tag.color + "44",
                            color: active ? tag.color : "#9ca3af",
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                          {active && <CheckCircle2 className="h-3 w-3" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </fieldset>

            {/* Resumo dos filtros ativos */}
            {!audienceForm.use_selected && (
              <div className="rounded-lg bg-gray-50 border border-purple-100 px-4 py-3 text-[11px] text-gray-500 leading-relaxed">
                {t("Serão incluídos leads")}
                {audienceForm.qualification_status === "qualified" && (
                  <span className="font-black text-green-600">
                    {" "}
                    {t("qualificados")}
                  </span>
                )}
                {audienceForm.qualification_status === "disqualified" && (
                  <span className="font-black text-red-500">
                    {" "}
                    {t("desqualificados")}
                  </span>
                )}
                {!audienceForm.qualification_status && (
                  <span className="font-black text-gray-700">
                    {" "}
                    {t("de qualquer qualificação")}
                  </span>
                )}
                {(audienceForm.pipeline_id
                  ? audienceForm.deal_columns
                  : audienceForm.statuses
                ).length > 0 && (
                  <>
                    {" "}
                    {t("na etapa")}{" "}
                    <span className="font-black text-purple-700">
                      {(audienceForm.pipeline_id
                        ? audienceForm.deal_columns
                        : audienceForm.statuses
                      )
                        .map(
                          (key) =>
                            audienceStageOptions.find((o) => o.key === key)
                              ?.label || key,
                        )
                        .join(", ")}
                    </span>
                  </>
                )}
                {audienceForm.tag_ids.length > 0 && (
                  <>
                    {" "}
                    {t("com as tags")}{" "}
                    <span className="font-black text-blue-600">
                      {audienceForm.tag_ids
                        .map(
                          (id) => availableTags.find((t) => t.id === id)?.name,
                        )
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </>
                )}
                {t(", sem fraudes e sem cancelados.")}
              </div>
            )}

            {/* Info */}
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-[11px] text-blue-600 leading-relaxed">
              {t(
                "Email e telefone são enviados ao Meta com hash SHA-256. O tamanho final do público depende do match rate da plataforma.",
              )}
            </div>
          </div>
        </Modal>

        <CampaignLeadDetailsModal
          lead={viewingLead}
          onClose={() => setViewingLead(null)}
          onSyncCRM={handleSyncToCRM}
          onSyncCapi={handleSyncCapi}
          onQualificationChange={handleQualificationChange}
          onStageChange={handleStageChange}
          onLeadUpdate={handleLeadUpdate}
          onTagsChange={handleModalTagsChange}
          kanbanStages={kanbanStages}
          availableTags={availableTags}
          offlineSources={OFFLINE_LEAD_SOURCES}
          onSendToLeadflow={handleSendToLeadflow}
        />
      </div>
    </PageLayout>
  );
};

export default CampaignLeads;
